/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import cs from 'classnames';
import { Field, Form, Formik } from 'formik';
import { isEmpty } from 'lodash';

import InputGroup from 'features/accountPlatforms/components/molecules/FacebookAdInputGroup/FacebookAdInputGroup';
import AdsetSelect from 'features/accountPlatforms/facebook/AdsetSelect';
import { AdCountLedger } from 'features/accountPlatforms/facebook/Campaigns';
import {
  adCreationSchema,
  AdCreative,
  BaseAdCreativeFormData,
  carouselAdCreationSchema,
  deriveFacebookAspectRatio,
  StandardAdCreative,
  StandardAdCreativeFormData,
} from 'features/accountPlatforms/facebook/components/AdCreationForm';
import LoadConfigurationBar from 'features/accountPlatforms/facebook/LoadConfigurationBar';
import { useConfigurations } from 'features/accountPlatforms/facebook/useConfigurations';
import { useAccountPlatforms } from 'features/accountPlatforms/useAccountPlatforms';
import CenteredSpinner from 'features/ui/CenteredSpinner';
import BackButton from 'shared/components/atoms/BackButton';
import FormikErrorFocus from 'shared/components/atoms/FormikErrorFocus';
import TrackDirtyState from 'shared/components/atoms/TrackDirtyState';
import { Box, Flex, Text } from 'shared/components/display';
import FormButton from 'shared/components/FormButton';
import SaveConfigurationForm from 'shared/components/forms/SaveConfigurationForm/SaveConfigurationForm';
import Heading from 'shared/components/molecules/Heading';
import Modal from 'shared/components/molecules/Modal';
import ParameterizedTextInput from 'shared/components/molecules/ParameterizedTextInput';
import { SelectInput } from 'shared/components/molecules/Select';
import TextInput from 'shared/components/TextInput';
import ToggleInput from 'shared/components/ToggleInput';

import { apiAction } from 'shared/actions/api';
import {
  AD_NAME,
  ADS,
  BODY,
  CALL_TO_ACTION,
  DESCRIPTION,
  DISPLAY_URL,
  HEADLINE,
  LANDING_PAGE,
  URL_TAGS,
} from 'shared/config/ads';
import { ACCOUNT_PLATFORM_COPY, COLLECTION_COPY, FACEBOOK_COPY } from 'shared/config/copy';
import { SIZING } from 'shared/config/formatting';
import { RESOURCES } from 'shared/config/resourceNames';
import { API_ACCOUNT_ROUTES } from 'shared/config/routes/api/apiAccountRoutes';
import { API_FACEBOOK_ROUTES } from 'shared/config/routes/api/apiFacebookRoutes';
import { ACCOUNT_PLATFORM_TOASTS } from 'shared/config/toasts/accountPlatformToasts';
import { useComponentLoading } from 'shared/hooks/useComponentLoading';
import { useFormData } from 'shared/hooks/useFormData';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import { breakpoints } from 'shared/styles/styleFunctions';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { BrkfstFile } from 'shared/typings/file';
import { formatDateNoSlashes } from 'shared/utilities/dateUtility';
import { LinkCreator } from 'shared/utilities/linkUtility';
import { parseDynamicString } from 'shared/utilities/stringUtility';
import { isImage } from 'shared/utilities/validator/utils';

import {
  AD_CREATION_INITIAL_VALUES,
  ASSET_INITIAL_VALUES,
  CALL_TO_ACTION_OPTIONS,
  CAROUSEL_AD_DYNAMIC_VARIABLES,
  CAROUSEL_INITIAL_VALUES,
  CREATOR_DYNAMIC_VARIABLES,
  FACEBOOK_URL_DYNAMIC_VARIABLES,
  STANDARD_AD_DYNAMIC_VARIABLES,
} from './config';
import { multipleAdsCreationFormStyles } from './multipleAdsCreationFormStyles';
import { MultipleUploadPrompt } from './multipleAdsCreationPrompt';
import { parseFacebookDynamicUrlString } from './utils';
import { collectionAdCreationSchema } from './validation';

const { FACEBOOK } = ADS;
const { H4 } = TEXT_VARIANTS;

const BODY_LABEL = 'Primary Text';
const CALL_TO_ACTION_LABEL = 'Call to Action';
const DISPLAY_URL_LABEL = 'Display Url';
const AD_NAME_LABEL = 'Ads Name';
const URL_TAGS_LABEL = 'Url Tags';
const LANDING_PAGE_LABEL = 'Landing Page URL';
const HEADLINE_LABEL = 'Headline';
const DESCRIPTION_LABEL = 'Description';
const ADSETS_LABEL = 'Select Adsets';
const BRKFST_LABEL = 'Add _brkfst suffix to Ads Name';

const BRKFST_TAG = '_brkfst';
const BRKFST_FIELD_NAME = 'hasBrkfstTag';
const CHECKOUT_ENTITY = 'facebookCheckout';

const FACEBOOK_AD_NAME_LIMIT = 400;

type LaunchableAd = AdCreative & { type?: number };

interface State {
  ads: LaunchableAd[];
  adCountErrors: AdCountLedger[];
  isUploadOpen: boolean;
  isSaveConfigOpen: boolean;
}

interface LocationState {
  selectedAssets: BrkfstFile[];
  type: number;
  isBriefCollection: boolean;
  collectionId: number;
}

/*
 * Handle multiple ad creation in FB (including assets from multiple creators)
 */
const FbMultipleAdsCreation = () => {
  const navigate = useNavigate();
  const location = useLocation<any>();
  const params = useParams<any>();
  const dispatch = useDispatch();

  const { organizationId, accountId, accountPlatformId } = params;
  const { loading } = useComponentLoading(RESOURCES.FACEBOOK, false);
  const { loading: adsetLoading } = useComponentLoading('Adset');
  const { selectedAssets, type, isBriefCollection, collectionId }: LocationState = useMemo(
    () => location?.state || {},
    [location],
  );
  const { accountPlatform } = useAccountPlatforms({
    accountPlatformId: +accountPlatformId,
  });
  const { fetchConfigurations, defaultConfiguration, updateConfiguration } = useConfigurations();

  const { savedData } = useFormData();

  const isCarousel = type === FACEBOOK.TYPES.CAROUSEL;
  const isCollection = type === FACEBOOK.TYPES.COLLECTION;
  const defaultConfig = defaultConfiguration.value;
  const isSystemAccount = !accountPlatform.type;
  const carouselAssets = defaultConfig.carouselAssets;

  const [state, setState] = useState<State>({
    ads: [],
    adCountErrors: [],
    isUploadOpen: false,
    isSaveConfigOpen: false,
  });
  const [touched, setTouched] = useState(false);

  const resetState = () => {
    setState((prev) => ({
      ...prev,
      ads: [],
      adCountErrors: [],
      isUploadOpen: false,
      isSaveConfigOpen: false,
    }));
  };
  const { ads, adCountErrors, isUploadOpen, isSaveConfigOpen } = state;

  const pathToAssetLibrary = useMemo(
    () =>
      LinkCreator.createLink({
        routeKey: 'ASSETS_LIBRARY',
        variables: {
          accountId,
          organizationId,
          accountPlatformId,
        },
      }),
    [accountId, accountPlatformId, organizationId],
  );

  const isMobile = useMediaQuery({
    maxWidth: breakpoints.md - 1,
  });

  /**
   * Based off previous-code isolated redirect use-effect.
   */
  useEffect(() => {
    if (!isSystemAccount || !type || isEmpty(selectedAssets)) {
      navigate(pathToAssetLibrary);
    }
  }, [selectedAssets, type, isSystemAccount, pathToAssetLibrary]);

  useEffect(() => {
    fetchConfigurations({
      entityType: 'account_platforms',
      entityId: accountPlatformId,
    });
  }, [accountPlatformId, type]);

  const openConfigModal = () => {
    setState((prev) => ({ ...prev, isSaveConfigOpen: true }));
  };

  const closeConfigModal = () => {
    setState((prev) => ({ ...prev, isSaveConfigOpen: false }));
  };

  const launchAds = (formData: BaseAdCreativeFormData) => {
    const adsByType = ads.reduce((acc, ad) => {
      if (!ad.type) throw new Error('Each Ad needs to be assigned an Ad Type (Video, Image, Carousel, Collection)');
      const key = ad.type;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(ad);
      return acc;
    }, {});
    const data: Record<string, any> = Object.keys(adsByType).map((type) => ({
      type,
      ads: adsByType[type],
      adsetIds: formData.adsets.map(({ value }) => value),
    }));

    dispatch(
      apiAction({
        path: {
          route: API_ACCOUNT_ROUTES.AD_LAUNCH,
          variables: {
            accountId,
          },
        },
        params: {
          accountPlatformId,
        },
        data,
        method: 'POST',
        onSuccess: ({ data: adRequestIds }) => {
          dispatch(
            apiAction({
              path: {
                route: API_ACCOUNT_ROUTES.AD_PROCESSING,
                variables: {
                  accountId,
                },
              },
              method: 'POST',
              data: { adRequestIds },
              navigate,
              entity: CHECKOUT_ENTITY,
              pushFunction: () => ({
                pathname: LinkCreator.createLink({
                  routeKey: 'FB_CHECKOUT_CONFIRMATION',
                  variables: {
                    accountId,
                    organizationId,
                    accountPlatformId,
                  },
                }),
                state: {
                  heading: FACEBOOK_COPY.LABEL_AD_PUBLISHED,
                  body: FACEBOOK_COPY.CHECKOUT_SUCCESS_BODY,
                  buttonLink: LinkCreator.createLink({
                    routeKey: 'PLATFORM',
                    variables: {
                      accountId,
                      organizationId,
                      accountPlatformId,
                    },
                  }),
                  buttonText: FACEBOOK_COPY.CHECKOUT_SUCCESS_BUTTON,
                },
              }),
            }),
          );
        },
      }),
    );
  };

  const hasSubmittedAssets = useMemo(() => selectedAssets.some((asset) => asset.submitted), [selectedAssets]);

  const formatAdName = (adName: string, tag: string, hasTag: boolean): string => {
    // prevent duplicating the _brkfst suffix
    return hasTag ? adName.replace(tag, '').concat(tag) : adName.replace(tag, '');
  };

  const createStandardAdCreative = (formData: StandardAdCreativeFormData, creatorAssets): StandardAdCreative => {
    const dynamicStringMap: Record<string, any> = { ...formData, date: formatDateNoSlashes(new Date()) };
    const { creatorFirstName: firstName, creatorLastName: lastName, creatorId, moduleNum } = creatorAssets[0];
    formData.adName = formatAdName(formData.adName, BRKFST_TAG, formData.hasBrkfstTag);

    if (hasSubmittedAssets && firstName && lastName) {
      const lastNameFirstName = `${lastName}${firstName}`;
      dynamicStringMap.creatorName = lastNameFirstName.replace(/\s/g, '');
      dynamicStringMap.moduleNumber = moduleNum;
      dynamicStringMap.creatorInitials = `${firstName[0]}${lastName[0]}`;
    }
    if (isBriefCollection && creatorId) {
      dynamicStringMap.creatorId = creatorId;
    }

    return {
      ...formData,
      callToAction: formData.callToAction.value,
      adName: parseDynamicString(formData.adName, dynamicStringMap),
      urlTags: parseFacebookDynamicUrlString(formData.urlTags, dynamicStringMap),
      assets: creatorAssets.map((asset) => ({
        fileId: asset.id,
        fileUrl: asset.url,
        fileName: asset.name,
        thumbnailUrl: asset.thumbnailUrl,
        labels: asset.tagNames,
        aspectRatio: deriveFacebookAspectRatio(asset.metadata.width, asset.metadata.height),
      })),
    };
  };

  const promptOnClick = (formData: any) => {
    const assetsByCreatorAndModule: Record<string, BrkfstFile[]> = selectedAssets.reduce((acc, asset) => {
      const { creatorId, moduleId } = asset;
      const key = `${creatorId}-${moduleId}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(asset);
      return acc;
    }, {});

    const ads = Object.keys(assetsByCreatorAndModule).map((key) => {
      const assets = assetsByCreatorAndModule[key];
      const allImages = assets.every(({ mimeType }) => isImage(mimeType));
      const allGifOrVideos = assets.every(({ mimeType }) => !isImage(mimeType));
      if (allImages || allGifOrVideos) {
        const standardAdCreative = createStandardAdCreative(formData, assets);
        return { ...standardAdCreative, type: allImages ? FACEBOOK.TYPES.IMAGE : FACEBOOK.TYPES.VIDEO };
      } else throw new Error('All assets belonging to the same submission module must be the same file type');
    });
    dispatch(
      apiAction({
        path: {
          route: API_FACEBOOK_ROUTES.ADSET_AD_COUNT,
        },
        params: {
          adsetIds: formData.adsets.map(({ value }) => value),
          accountPlatformId,
        },
        entity: 'Adset',
        onSuccess: ({ data: adCounts }) => {
          setState((prev) => ({
            ...prev,
            ads: ads,
            adCountErrors: adCounts.filter(({ total, adLimit }) => total + 1 > adLimit),
          }));
        },
      }),
    );
    setState((prev) => ({
      ...prev,
      isUploadOpen: true,
    }));
  };

  const creatorDynamicVariables = useMemo(
    () => (hasSubmittedAssets ? CREATOR_DYNAMIC_VARIABLES : []),
    [hasSubmittedAssets],
  );

  const adNameDynamicVariables = useMemo(() => {
    if (isCarousel) return creatorDynamicVariables.concat(CAROUSEL_AD_DYNAMIC_VARIABLES);
    return creatorDynamicVariables.concat(STANDARD_AD_DYNAMIC_VARIABLES);
  }, [isCarousel, creatorDynamicVariables]);

  const urlDynamicVariables = useMemo(() => {
    if (isCarousel)
      return creatorDynamicVariables.concat(CAROUSEL_AD_DYNAMIC_VARIABLES).concat(FACEBOOK_URL_DYNAMIC_VARIABLES);
    return creatorDynamicVariables.concat(STANDARD_AD_DYNAMIC_VARIABLES).concat(FACEBOOK_URL_DYNAMIC_VARIABLES);
  }, [isCarousel, creatorDynamicVariables]);

  const initialValues = useMemo(() => {
    const defaultValues = {
      adsets: [], // Required for adset multi-select, at-top incase overridden below
      productSet: null,
      ...(isCarousel ? CAROUSEL_INITIAL_VALUES : AD_CREATION_INITIAL_VALUES),
      ...savedData,
      ...defaultConfig,
    };
    if (isCarousel) {
      if (defaultConfig.carouselAssets) {
        defaultValues.carouselAssets = carouselAssets.map((carAsset, i) => {
          return { ...carAsset, asset: selectedAssets[i] };
        });
      } else {
        defaultValues.carouselAssets = selectedAssets.map((asset) => ({
          asset,
          ...ASSET_INITIAL_VALUES,
        }));
      }
    } else {
      defaultValues.assets = selectedAssets;
    }
    return defaultValues;
  }, [isCarousel, savedData, defaultConfig, carouselAssets, selectedAssets]);

  const validationSchema = useMemo(() => {
    if (isCarousel) return carouselAdCreationSchema;
    else if (isCollection) return collectionAdCreationSchema;
    return adCreationSchema;
  }, [isCarousel, isCollection]);

  return (
    <Flex css={multipleAdsCreationFormStyles}>
      <BackButton
        to={LinkCreator.createLink({
          routeKey: 'COLLECTION',
          variables: {
            accountId,
            organizationId,
            collectionId,
          },
        })}
        state={{ selectedFileIds: selectedAssets.map(({ id }) => id) }}
      >
        {COLLECTION_COPY.BUTTON_BACK_TO_COLLECTION}
      </BackButton>
      <CenteredSpinner visible={loading} className={cs({ 'multiple-ads-creation-form--hidden': !loading })} />
      <div className={cs({ 'multiple-ads-creation-form--hidden': loading })}>
        <Text variant={H4} className="multiple-ads-creation-form__heading">
          {FACEBOOK_COPY.HEADING_FORM}
        </Text>
        <Formik
          enableReinitialize
          validateOnBlur={false}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => {
            promptOnClick(values);
            actions.setSubmitting(false);
          }}
        >
          {(formikProps) => (
            <Form className="multiple-ads-creation-form">
              <Flex className="multiple-ads-creation-form__top-row">
                <LoadConfigurationBar isMobile={isMobile} />
                <FormButton>Upload Ads</FormButton>
              </Flex>

              <Flex className={'multiple-ads-creation-form__container'}>
                <TrackDirtyState setDirtyState={setTouched} />
                <FormikErrorFocus offset={30} formikProps={formikProps} />
                <Text variant={H4} className="multiple-ads-creation-form__heading">
                  {FACEBOOK_COPY.HEADING_FORM_SETTINGS}
                </Text>
                <AdsetSelect name="adsets" placeholder={ADSETS_LABEL} accountPlatformId={accountPlatformId} />
                <StandardInputGroup />
                <Flex>
                  <Field
                    className="multiple-ads-creation-form__call-to-action"
                    name={CALL_TO_ACTION}
                    component={SelectInput}
                    options={CALL_TO_ACTION_OPTIONS}
                    placeholder={CALL_TO_ACTION_LABEL}
                    width="100%"
                  />
                  <Field
                    className="multiple-ads-creation-form__display-url"
                    name={DISPLAY_URL}
                    component={TextInput}
                    label={DISPLAY_URL_LABEL}
                    placeholder={DISPLAY_URL_LABEL}
                  />
                </Flex>
                <Heading
                  className="multiple-ads-creation-form__dynamic-vars-heading"
                  text={FACEBOOK_COPY.HEADING_FORM_DYNAMIC_VARIABLES}
                  variant={TEXT_VARIANTS.H6}
                  descriptionText={FACEBOOK_COPY.FORM_DESCRIPTION_DYNAMIC_VARIABLES}
                />
                <ParameterizedTextInput
                  name={AD_NAME}
                  maxLength={FACEBOOK_AD_NAME_LIMIT}
                  variables={adNameDynamicVariables}
                  label={AD_NAME_LABEL}
                  placeholder={AD_NAME_LABEL}
                />
                <Field
                  name={BRKFST_FIELD_NAME}
                  className="multiple-ads-creation-form__brkfst-suffix"
                  checkboxClassName="multiple-ads-creation-form__brkfst-suffix__input"
                  component={ToggleInput}
                  label={BRKFST_LABEL}
                />
                <ParameterizedTextInput
                  name={URL_TAGS}
                  variables={urlDynamicVariables}
                  label={URL_TAGS_LABEL}
                  placeholder={URL_TAGS_LABEL}
                />
                <Flex className="multiple-ads-creation-form__form-buttons">
                  <FormButton
                    type="button"
                    onClick={() =>
                      updateConfiguration(
                        defaultConfiguration?.id,
                        {
                          ...defaultConfiguration,
                          type: `ad-creation-${type}`,
                          entityType: 'account_platforms',
                          entityId: accountPlatformId,
                          value: { ...formikProps.values },
                        },
                        ACCOUNT_PLATFORM_TOASTS.CONFIGURATION_UPDATE_SUCCESS,
                      )
                    }
                    variant={BUTTON_VARIANTS.OUTLINE}
                    disabled={!touched || !defaultConfiguration?.id}
                  >
                    Save Changes
                  </FormButton>
                  <FormButton
                    type="button"
                    onClick={openConfigModal}
                    variant={BUTTON_VARIANTS.PRIMARY}
                    disabled={!touched}
                  >
                    Save as New Settings
                  </FormButton>
                </Flex>

                <Modal
                  isOpen={isUploadOpen}
                  title={FACEBOOK_COPY.PROMPT_MULTIPLE_ADS_UPLOAD_TITLE}
                  modalSize={SIZING.MEDIUM_WIDE}
                  component={
                    <MultipleUploadPrompt
                      ads={ads}
                      loading={adsetLoading}
                      adCountErrors={adCountErrors}
                      onClick={() => {
                        launchAds(formikProps.values);
                      }}
                      onRequestClose={resetState}
                    />
                  }
                  onRequestClose={resetState}
                />
                <Modal
                  isOpen={isSaveConfigOpen}
                  title={ACCOUNT_PLATFORM_COPY.FORM_HEADING_SAVE_CONFIG}
                  component={
                    <SaveConfigurationForm
                      type={`ad-creation-${type}`}
                      formData={formikProps.values}
                      entityType="account_platforms"
                      entityId={accountPlatformId}
                      onRequestClose={closeConfigModal}
                    />
                  }
                  onRequestClose={closeConfigModal}
                />
              </Flex>
            </Form>
          )}
        </Formik>
      </div>
    </Flex>
  );
};

const StandardInputGroup: React.FC = () => (
  <Box>
    <InputGroup name={LANDING_PAGE} label={LANDING_PAGE_LABEL} hideLabel flipFields />
    <InputGroup
      name={HEADLINE}
      label={HEADLINE_LABEL}
      softMaxLength={27}
      maxLength={255}
      labelMaxLength={255}
      hideLabel
      flipFields
    />
    <InputGroup name={BODY} label={BODY_LABEL} softMaxLength={72} hideLabel flipFields />
    <InputGroup
      name={DESCRIPTION}
      label={DESCRIPTION_LABEL}
      softMaxLength={27}
      maxLength={10000}
      labelMaxLength={255}
      hideLabel
      flipFields
    />
  </Box>
);

export default FbMultipleAdsCreation;
